import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/client';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';

export default function TransactionHistory() {
  const { t } = useTranslation();
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Filter state
  const [filterType, setFilterType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Custom dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filterOptions = [
    { value: '', label: t('transactionHistory.filters.allTypes', 'All Types') },
    { value: 'addition', label: t('transactionHistory.filters.addition', 'Additions') },
    { value: 'usage', label: t('transactionHistory.filters.usage', 'Usage') },
    { value: 'expiry', label: t('transactionHistory.filters.expiry', 'Expiries') },
    { value: 'adjustment', label: t('transactionHistory.filters.adjustment', 'Adjustments') },
  ];

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: page,
        page_size: pageSize,
      });
      
      if (filterType) params.append('entry_type', filterType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const res = await api.get(`credits/ledger/?${params.toString()}`);
      
      // DRF PageNumberPagination returns { count, next, previous, results }
      setLedger(res.data.results || []);
      setTotalPages(Math.ceil((res.data.count || 0) / pageSize));
    } catch (err) {
      console.error('Failed to fetch ledger:', err);
      setError(t('transactionHistory.errors.loadFailed', 'Unable to load transaction history.'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterType, startDate, endDate, t]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  const handleFilterChange = () => {
    setPage(1);
    fetchLedger();
  };

  const clearFilters = () => {
    setFilterType('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const getEntryIcon = (type) => {
    switch (type) {
      case 'addition':
      case 'adjustment':
        return <CheckCircleOutlineIcon style={{ color: '#10b981', fontSize: 18 }} />;
      case 'usage':
        return <AccessTimeIcon style={{ color: '#f59e0b', fontSize: 18 }} />;
      case 'expiry':
        return <AutorenewIcon style={{ color: '#ef4444', fontSize: 18 }} />;
      default:
        return <CheckCircleOutlineIcon style={{ color: '#3b82f6', fontSize: 18 }} />;
    }
  };

  const getAmountStyle = (amount, type) => {
    if (amount > 0 || type === 'addition' || type === 'adjustment') {
      return { color: '#10b981', prefix: '+' };
    }
    return { color: '#171717', prefix: '' };
  };

  const formatEntryType = (type) => {
    if (!type) return t('transactionHistory.types.unknown', 'Unknown');
    return t(`transactionHistory.types.${type}`, type.charAt(0).toUpperCase() + type.slice(1));
  };

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 gap-4">
        <div className="flex items-center gap-2">
          <div
            className="p-2 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(139,92,246,0.1) 100%)',
            }}
          >
            <span className="text-base">📝</span>
          </div>
          <h3 className="text-lg font-bold" style={{ color: '#171717' }}>
            {t('transactionHistory.title', 'Transaction History')}
          </h3>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="text-sm px-4 py-2 rounded-lg border bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between min-w-[140px]"
              style={{ borderColor: 'rgba(229,229,229,1)', color: '#404040' }}
            >
              <span className="font-medium">{filterOptions.find(opt => opt.value === filterType)?.label || t('transactionHistory.filters.allTypes', 'All Types')}</span>
              <svg className={`w-4 h-4 ml-2 transition-transform duration-200 text-gray-500 ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </button>
            
            {dropdownOpen && (
              <div 
                className="absolute top-full left-0 mt-1.5 w-full bg-white border border-gray-100 rounded-xl shadow-lg z-50 py-1.5 overflow-hidden"
                style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
              >
                {filterOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setFilterType(opt.value);
                      setDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${filterType === opt.value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <span>{opt.label}</span>
                    {filterType === opt.value && (
                      <CheckCircleOutlineIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center border rounded-lg bg-white overflow-hidden shadow-sm" style={{ borderColor: 'rgba(229,229,229,1)' }}>
            <div className="flex items-center px-3 py-2 border-r hover:bg-gray-50 transition-colors relative group" style={{ borderColor: 'rgba(229,229,229,1)' }}>
              <span className="text-[11px] text-gray-400 uppercase tracking-widest font-bold mr-2">{t('transactionHistory.filters.from', 'From')}</span>
              <div className="flex items-center">
                <span className="text-sm font-medium whitespace-nowrap" style={{ color: startDate ? '#3b82f6' : '#9ca3af' }}>
                  {startDate ? new Date(startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : t('transactionHistory.filters.datePlaceholder', 'Date')}
                </span>
                <svg className={`w-4 h-4 ml-2 transition-colors ${startDate ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
              </div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                onClick={(e) => {
                  try { e.target.showPicker(); } catch (err) {}
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
            
            <div className="flex items-center px-3 py-2 hover:bg-gray-50 transition-colors relative group">
              <span className="text-[11px] text-gray-400 uppercase tracking-widest font-bold mr-2">{t('transactionHistory.filters.to', 'To')}</span>
              <div className="flex items-center">
                <span className="text-sm font-medium whitespace-nowrap" style={{ color: endDate ? '#3b82f6' : '#9ca3af' }}>
                  {endDate ? new Date(endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : t('transactionHistory.filters.datePlaceholder', 'Date')}
                </span>
                <svg className={`w-4 h-4 ml-2 transition-colors ${endDate ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
              </div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                onClick={(e) => {
                  try { e.target.showPicker(); } catch (err) {}
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
          </div>
          {(filterType || startDate || endDate) && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {t('transactionHistory.buttons.clear', 'Clear')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {/* Table container */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(229,229,229,0.5)' }}>
              <th className="pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('transactionHistory.headers.date', 'Date')}</th>
              <th className="pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('transactionHistory.headers.type', 'Type')}</th>
              <th className="pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('transactionHistory.headers.description', 'Description')}</th>
              <th className="pb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">{t('transactionHistory.headers.amount', 'Amount')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="py-8 text-center text-sm text-gray-500">
                  {t('transactionHistory.status.loading', 'Loading history...')}
                </td>
              </tr>
            ) : ledger.length === 0 ? (
              <tr>
                <td colSpan="4" className="py-8 text-center text-sm text-gray-500">
                  {t('transactionHistory.status.noTransactions', 'No transactions found.')}
                </td>
              </tr>
            ) : (
              ledger.map((entry) => {
                const { color, prefix } = getAmountStyle(entry.amount, entry.entry_type);
                return (
                  <tr key={entry.id} style={{ borderBottom: '1px solid rgba(229,229,229,0.5)' }} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 text-sm font-medium text-gray-700">
                      {new Date(entry.created_at).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        {getEntryIcon(entry.entry_type)}
                        <span className="text-sm font-semibold text-gray-800">
                          {formatEntryType(entry.entry_type)}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-600">
                      {entry.notes || (entry.entry_type === 'addition' ? t('transactionHistory.defaultNotes.dailyAdded', 'Daily credits added') : t('transactionHistory.defaultNotes.creditUpdate', 'Credit update'))}
                    </td>
                    <td className="py-4 text-sm font-bold text-right" style={{ color }}>
                      {prefix}{entry.amount}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4" style={{ borderTop: '1px solid rgba(229,229,229,0.5)' }}>
          <span className="text-sm text-gray-500">
            {t('transactionHistory.pagination.page', 'Page')}{' '}
            <span className="font-semibold text-gray-800">{page}</span>{' '}
            {t('transactionHistory.pagination.of', 'of')}{' '}
            <span className="font-semibold text-gray-800">{totalPages}</span>
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`p-2 rounded-lg transition-colors flex items-center justify-center ${page === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <ArrowBackIosIcon style={{ fontSize: 14 }} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className={`p-2 rounded-lg transition-colors flex items-center justify-center ${page === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <ArrowForwardIosIcon style={{ fontSize: 14 }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
